// src/shell/Sidebar.jsx
import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  History, Settings, Zap,
  BookOpenText, LayoutGrid, BookMarked, UsersRound, SquareUser, Speech,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext.jsx';
import useEntitlements from '../hooks/useEntitlements.js';
import { formatCredits, consumedFraction } from '../utils/creditsFormat.js';
import { COLORS, FONT_SANS, RADII } from '../constants/theme.js';

// Storico Generazioni glyph (designer asset) — clock with rewind arrow. fill=currentColor
// so it inherits the nav item's active/inactive color like the lucide icons.
const StoricoIcon = ({ size = 18, style }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" style={style} xmlns="http://www.w3.org/2000/svg">
    <path d="M10.625 6.24999V9.64609L13.4469 11.3391C13.589 11.4244 13.6914 11.5628 13.7316 11.7236C13.7717 11.8845 13.7463 12.0547 13.6609 12.1969C13.5756 12.339 13.4372 12.4414 13.2764 12.4816C13.1155 12.5217 12.9453 12.4963 12.8031 12.4109L9.67812 10.5359C9.58563 10.4804 9.50911 10.4018 9.45599 10.3079C9.40287 10.214 9.37497 10.1079 9.375 9.99999V6.24999C9.375 6.08423 9.44085 5.92526 9.55806 5.80805C9.67527 5.69084 9.83424 5.62499 10 5.62499C10.1658 5.62499 10.3247 5.69084 10.4419 5.80805C10.5592 5.92526 10.625 6.08423 10.625 6.24999ZM10 2.49999C9.01406 2.49754 8.03742 2.69064 7.12661 3.06814C6.21579 3.44563 5.38889 4.00002 4.69375 4.69921C4.12578 5.27421 3.62109 5.82734 3.125 6.40624V4.99999C3.125 4.83423 3.05915 4.67526 2.94194 4.55805C2.82473 4.44084 2.66576 4.37499 2.5 4.37499C2.33424 4.37499 2.17527 4.44084 2.05806 4.55805C1.94085 4.67526 1.875 4.83423 1.875 4.99999V8.12499C1.875 8.29075 1.94085 8.44972 2.05806 8.56693C2.17527 8.68414 2.33424 8.74999 2.5 8.74999H5.625C5.79076 8.74999 5.94973 8.68414 6.06694 8.56693C6.18415 8.44972 6.25 8.29075 6.25 8.12499C6.25 7.95923 6.18415 7.80026 6.06694 7.68305C5.94973 7.56584 5.79076 7.49999 5.625 7.49999H3.82812C4.38672 6.84218 4.94297 6.22265 5.57734 5.58046C6.44598 4.71183 7.55133 4.11844 8.75529 3.87443C9.95924 3.63042 11.2084 3.74662 12.3467 4.2085C13.485 4.67038 14.4619 5.45746 15.1555 6.47141C15.849 7.48535 16.2283 8.68118 16.2461 9.90948C16.2639 11.1378 15.9193 12.3441 15.2554 13.3777C14.5915 14.4113 13.6377 15.2263 12.5132 15.7209C11.3888 16.2155 10.1435 16.3678 8.93299 16.1587C7.72248 15.9496 6.60043 15.3884 5.70703 14.5453C5.64732 14.4889 5.57708 14.4448 5.50032 14.4155C5.42356 14.3862 5.34179 14.3723 5.25967 14.3746C5.17754 14.377 5.09668 14.3954 5.0217 14.429C4.94672 14.4626 4.87908 14.5106 4.82266 14.5703C4.76623 14.63 4.72212 14.7003 4.69283 14.777C4.66355 14.8538 4.64967 14.9355 4.652 15.0177C4.65432 15.0998 4.67279 15.1807 4.70636 15.2556C4.73993 15.3306 4.78795 15.3983 4.84766 15.4547C5.73785 16.2947 6.82012 16.9042 8 17.2298C9.17989 17.5554 10.4215 17.5872 11.6166 17.3226C12.8116 17.058 13.9237 16.5049 14.8559 15.7117C15.788 14.9184 16.5118 13.9091 16.9642 12.7717C17.4165 11.6344 17.5836 10.4036 17.4509 9.18686C17.3182 7.97008 16.8897 6.80428 16.2029 5.79119C15.516 4.7781 14.5916 3.94851 13.5104 3.37482C12.4292 2.80114 11.224 2.50079 10 2.49999Z" fill="currentColor" />
  </svg>
);

// Sidebar expand glyph (designer asset) — panel outline with a left rail. Drawn
// on the same 44×44 canvas as the brand glyph so the two swap without a jump.
const ExpandIcon = ({ className, size = 40, style }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 44 44" fill="none" style={style} xmlns="http://www.w3.org/2000/svg">
    <path d="M29 15.4H15.25C14.9185 15.4 14.6005 15.5317 14.3661 15.7661C14.1317 16.0006 14 16.3185 14 16.65V27.9C14 28.2315 14.1317 28.5495 14.3661 28.7839C14.6005 29.0183 14.9185 29.15 15.25 29.15H29C29.3315 29.15 29.6495 29.0183 29.8839 28.7839C30.1183 28.5495 30.25 28.2315 30.25 27.9V16.65C30.25 16.3185 30.1183 16.0006 29.8839 15.7661C29.6495 15.5317 29.3315 15.4 29 15.4ZM15.25 16.65H18.375V27.9H15.25V16.65ZM29 27.9H19.625V16.65H29V27.9Z" fill={COLORS.surface} />
  </svg>
);

// Map application.id → route path
const APP_ID_TO_PATH = {
  'content-generator': '/content-generator',
};

// nav id → lucide icon (Brian UI 1.0, per Figma sidebar). Unknown ids fall back to Zap.
//   Content Generator → open book/reader · Storico → history · Report/Overview → grid
//   Knowledge Base/Guida → bookmarked book · Gestione Workspace → users · Configurazione (system settings) → settings
//   Brand Voice → speech
const ICONS = {
  'dashboard': LayoutGrid,
  'content-generator': BookOpenText,
  'guide': BookMarked,
  'knowledge-base': BookMarked,
  'runs': StoricoIcon,
  'admin-overview': LayoutGrid,
  'admin-users': UsersRound,
  'admin-kb': BookMarked,
  'admin-personas': SquareUser,
  'admin-runs': History,
  'admin-system-settings': Settings,
  'tenant-admin-manage': UsersRound,
  'tenant-admin-kb': BookMarked,
  'tenant-admin-brand-voice': Speech,
  'tenant-admin-personas': SquareUser,
  'tenant-admin-runs': History,
};
const iconFor = (id) => ICONS[id] || Zap;

// Inactive nav item color — Figma renders all nav labels/icons white (#FFFFFF);
// the active item is distinguished only by the purple pill + bold weight.
const NAV_INACTIVE = COLORS.surface;

// Fixed items always shown first (labels resolved via t(`nav.${id}`) at render).
// Order follows the Figma sidebar: Content Generator first, then Report Attività
// (dashboard), then Guida.
const FIXED_ITEMS = [
  // ⛔ The "Carica esempi" child is gone with the Examples Library: the backend
  // route it called (GET /api/v1/examples) was removed on 2026-08-04 (q@363f0d8,
  // "automated-tenant-onboarding P0") together with the 27 curated YAML briefs.
  // That commit assumed the FE had already dropped the feature; it had not, so
  // the entry survived for a month and answered a raw 404 to anyone who clicked
  // it. ⚠️ It was hardcoded HERE, not driven by `app_features` — which is why
  // the removal plan's "no FE menu item is orphaned" check looked in the
  // database and found nothing.
  { id: 'content-generator', path: '/content-generator' },
  { id: 'dashboard', path: '/dashboard' },
  { id: 'guide', path: '/guida' },
  // ⭐ Crediti (/credits) and Piani (/billing) MOVED to the avatar menu on
  // 2026-09-24, joining Profilo and Dati di fatturazione: they are account
  // pages, not daily work. The credits RAIL at the foot stays and now links
  // to /credits. ⚠️ The rail is a convenience, not a fallback — it renders
  // nothing in admin mode, with no tenant, on a failed fetch, or on a tenant
  // with no allowance, which is exactly the user most likely to want it.
];

function navStyle(isActive) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px 12px 14px',
    borderRadius: RADII.control,
    textDecoration: 'none',
    fontSize: '14px',
    lineHeight: '20px',
    fontFamily: FONT_SANS,
    color: isActive ? COLORS.surface : NAV_INACTIVE,
    background: isActive ? COLORS.accent : 'transparent',
    fontWeight: isActive ? 700 : 600,
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  };
}

// Hover brighten for non-active items (active items carry aria-current="page").
const hoverIn = (e) => {
  if (e.currentTarget.getAttribute('aria-current') === 'page') return;
  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
  e.currentTarget.style.color = COLORS.surface;
};
const hoverOut = (color) => (e) => {
  if (e.currentTarget.getAttribute('aria-current') === 'page') return;
  e.currentTarget.style.background = 'transparent';
  e.currentTarget.style.color = color;
};

const subNavStyle = {
  // display:flex + align-items live in the .sidebar-subnav CSS class so the
  // collapsed-rail rules toggle visibility without fighting an inline style.
  padding: '6px 12px 6px 42px',
  borderRadius: RADII.control,
  textDecoration: 'none',
  fontSize: '12px',
  fontFamily: FONT_SANS,
  color: COLORS.textTertiary,
  fontWeight: 500,
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
};

const divider = { border: 'none', borderTop: `1px solid rgba(255,255,255,0.08)`, margin: '10px 6px' };

// Group label ("BRIAN CORTEX"). Same type treatment as the credits rail's
// heading, so the two ends of the menu speak with one voice.
const sectionHeading = {
  padding: '18px 16px 6px',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: COLORS.textTertiary,
  fontFamily: FONT_SANS,
  whiteSpace: 'nowrap',
};

/**
 * The credits rail at the foot of the sidebar.
 *
 * ⭐ Reads the wallet through `useEntitlements()` rather than calling
 * /credits/balance itself. ⚠️ That hook is a shared refresh SIGNAL, not a
 * shared cache: each mounted instance holds its own state and its own fetch, so
 * this still costs one read — the same one the rail's private fetch used to
 * cost. What it buys is the epoch subscription: `refreshEntitlements()` after a
 * run refusal now re-reads here too, where the private version went stale after
 * every generation. ⛔ So do NOT read "one store" as licence to mount the hook
 * anywhere the number is wanted: a caller that already holds a /balance payload
 * must take the field off that, not mount a second reader.
 *
 * Same rules as CreditBalanceCard, from the same helpers: the figure is the
 * SPENDABLE total with no denominator (the wallet is two pots and the purchased
 * one has no ceiling), and the bar is consumed/allowance, clamped.
 *
 * ⛔ The rail draws NOTHING rather than something wrong: admin mode, no tenant, a
 * failed fetch (the hook fails open with nulls) or no allowance all render null.
 * /credits is where a blocked or planless state gets its sentence.
 */
function CreditsMeter() {
  const { t } = useTranslation('shell');
  const { adminMode, activeTenant } = useAuth();
  const {
    unlimited, remainingCredits: remaining, allowanceCredits: allowance,
    consumedCredits: consumed, loading,
  } = useEntitlements();

  // ⛔ `loading` alone must NOT blank the rail: every refreshEntitlements() flips
  // it back to true while KEEPING the previous numbers, so gating on it would
  // unmount the whole 113px block — border and padding included — and remount it
  // a moment later, shifting the nav above it on every run refusal. Only the
  // first load, where there is genuinely nothing to draw yet, renders null.
  if (adminMode || !activeTenant?.tenant_id) return null;
  if (loading && remaining == null && !unlimited) return null;
  const hasAllowance = !unlimited && allowance != null && remaining != null;
  if (!unlimited && !hasAllowance) return null;

  const fraction = consumedFraction(consumed, allowance);

  return (
    <Link
      to="/credits"
      className="sidebar-credits"
      aria-label={t('creditsMeter.goToCredits')}
      // ⛔ `display: block` lives in the .sidebar-credits CSS rule, NOT inline:
      // an inline display would beat `.sidebar.collapsed .sidebar-credits
      // { display: none }` and draw the rail inside the collapsed 72px icon bar.
      style={{
        textDecoration: 'none',
        padding: '24px 18px', flexShrink: 0,
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div style={{
        fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase',
        color: COLORS.textTertiary, fontFamily: FONT_SANS, marginBottom: '9px',
      }}>
        {t('creditsMeter.title')}
      </div>
      <div style={{ fontSize: '18px', fontWeight: 700, color: COLORS.surface, fontFamily: FONT_SANS }}>
        {unlimited ? t('creditsMeter.unlimited') : formatCredits(remaining)}
      </div>
      {fraction != null && (
        <div style={{ marginTop: '9px', height: '8px', borderRadius: RADII.pill, background: 'rgba(255,255,255,0.9)', overflow: 'hidden' }}>
          <div style={{ width: `${fraction * 100}%`, height: '100%', borderRadius: RADII.pill, background: COLORS.accent }} />
        </div>
      )}
    </Link>
  );
}

function NavItem({ item, labelOf }) {
  const Icon = iconFor(item.id);
  return (
    <NavLink
      to={item.path}
      // aria-label keeps the accessible name when the collapsed rail hides the
      // visible label with display:none (which removes it from the a11y tree).
      aria-label={labelOf(item)}
      style={({ isActive }) => navStyle(isActive)}
      onMouseEnter={hoverIn}
      onMouseLeave={hoverOut(NAV_INACTIVE)}
    >
      <Icon size={20} style={{ flexShrink: 0 }} />
      <span className="nav-label">{labelOf(item)}</span>
    </NavLink>
  );
}

export default function Sidebar({ open, collapsed = false, onToggleCollapse = () => {} }) {
  const { applications, isSuperadmin, isTenantAdmin, canManageWorkspaces, adminMode } = useAuth();
  const { t } = useTranslation('shell');

  // Resolve a nav item's display label: app items carry their own dynamic
  // display_name; all other items resolve via t(`nav.${id}`).
  const labelOf = (item) => item.label ?? t(`nav.${item.id}`);

  const HIDDEN_APP_IDS = new Set(['seo-rewrite']);
  const fixedIds = new Set(FIXED_ITEMS.map(i => i.id));
  const appItems = applications
    .filter(app => !fixedIds.has(app.id) && !HIDDEN_APP_IDS.has(app.id))
    .map(app => ({
      id: app.id,
      label: app.display_name,
      path: APP_ID_TO_PATH[app.id] || `/${app.id}`,
    }));

  // ── The two groups the menu is built from (design "Side menu", 2026-09-15) ──
  //
  // One unlabelled group for what the user DOES, then a labelled BRIAN CORTEX
  // group for the material the engine draws on. ⛔ The split is by MEANING, not
  // by permission: a member sees a Cortex group holding one item rather than no
  // heading, because the heading names the section and not its reader.
  //
  // ⚠️ The same entry resolves to a DIFFERENT path per role — a tenant-admin's
  // Knowledge Base and history live under /tenant-admin/*, a member's do not —
  // which is why these are built from the role rather than filtered from one
  // list. A superadmin gets neither: the admin console below carries its own.
  const tenantAdminView = isTenantAdmin && !adminMode && !isSuperadmin;
  const memberView = !isSuperadmin && !isTenantAdmin;

  const runsItem = tenantAdminView ? { id: 'tenant-admin-runs', path: '/tenant-admin/runs' }
    : memberView ? { id: 'runs', path: '/runs' }
      : null;

  const mainItems = [
    ...FIXED_ITEMS.filter(i => i.id === 'content-generator'),
    ...(runsItem ? [runsItem] : []),
    ...FIXED_ITEMS.filter(i => i.id !== 'content-generator'),
    // ⛔ NOT tenantAdminView. "Gestione Workspace" is the one entry driven by
    // canManageWorkspaces (any administered workspace, or a company claim) rather
    // than by the ACTIVE workspace: an admin of B whose active workspace is A must
    // still see it. KB / personas / runs below keep tenantAdminView — they act on
    // the active workspace, so hiding them when it is not administered is correct.
    ...(canManageWorkspaces && !adminMode ? [{ id: 'tenant-admin-manage', path: '/tenant-admin/manage' }] : []),
    ...appItems,
  ];

  // ⛔ No tenant "Configurazione" for workspace admins: the entry went on 2026-09-15 and the page itself on
  // 2026-09-27 — editing the workspace's configuration files directly is no longer allowed to them. The
  // superadmin reaches that editor («Configurazione tenant», /admin/context-settings) from the console's tab row.
  // Brand Voice and Personas exist for the admin of the ACTIVE workspace only
  // (no member entry, no superadmin mount), mirroring the backend's TenantAdmin gate.
  const cortexItems = [
    ...(tenantAdminView ? [{ id: 'tenant-admin-kb', path: '/tenant-admin/knowledge-base' }]
      : memberView ? [{ id: 'knowledge-base', path: '/knowledge-base' }] : []),
    ...(tenantAdminView ? [{ id: 'tenant-admin-brand-voice', path: '/tenant-admin/brand-voice' }] : []),
    ...(tenantAdminView ? [{ id: 'tenant-admin-personas', path: '/tenant-admin/personas' }] : []),
  ];

  const adminItems = isSuperadmin
    ? [
        { id: 'admin-overview', path: '/admin/overview' },
        { id: 'admin-users', path: '/admin/users' },
        { id: 'admin-kb', path: '/admin/knowledge-base' },
        { id: 'admin-personas', path: '/admin/personas' },
        { id: 'admin-runs', path: '/admin/runs' },
      ]
    : [];

  // «Configurazione» = SYSTEM settings (v219), last, after its own separator in BOTH modes. Superadmin-only, like
  // the console entries above (hidden, not greyed, for everyone else — the console is not theirs to request).
  const systemItems = isSuperadmin ? [{ id: 'admin-system-settings', path: '/admin/settings' }] : [];

  return (
    <>
      <style>{`
        /* Side menu (Figma "Side menu"). On desktop it's a 72px icon rail that
           expands to 260px on hover (or keyboard focus) and PUSHES the content
           along; on mobile it's an off-canvas drawer. Sections carry their own
           padding; the sidebar itself has none. */
        .sidebar {
          width: 260px;
          height: 100%;
          background: ${COLORS.ink};
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          transition: transform 0.25s ease, width 0.2s ease;
          overflow: hidden;
        }
        .sidebar-nav {
          display: flex;
          flex-direction: column;
          gap: 3px;
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 12px;
        }
        .sidebar-brand {
          display: flex; flex-direction: row; align-items: center;
          justify-content: flex-start; gap: 11px;
          padding: 24px 16px 12px; margin-bottom: 20px;
        }
        /* The logo doubles as the collapse/expand toggle, so it's a <button>. */
        .brand-lockup {
          display: flex; align-items: center; gap: 10px;
          width: 100%;
          border: none; background: transparent; padding: 0;
          cursor: pointer; text-align: left;
        }
        .brand-lockup:focus-visible { outline: 2px solid ${COLORS.accent}; outline-offset: 2px; border-radius: 8px; }
        .sidebar-subnav { display: flex; align-items: center; }
        /* The credits rail is a <Link> (to /credits); block it here rather than
           inline so the collapsed-rail rule below can still hide it. */
        .sidebar-credits { display: block; }
        .nav-section-rule { display: none; }
        /* Toggle affordance sitting next to the logo. Always visible while the
           sidebar is expanded; on the collapsed rail there's no room beside the
           glyph, so it's hidden and swapped in on hover (rules below). */
        .brand-expand-icon { display: block; margin-left: auto; }

        @media (min-width: 769px) {
          /* Desktop: full-width (260px) in-flow sidebar that collapses to a 72px
             icon rail when .collapsed (toggled by pressing the logo). Being
             in-flow, the page reflows WITH it — content is pushed/pulled as the
             rail toggles, no overlay. */
          .sidebar.collapsed { width: 72px; }

          /* Collapsed = icon-only: hide labels, wordmark, credits and sub-nav;
             centre the logo + icons. */
          .sidebar.collapsed .nav-label,
          .sidebar.collapsed .brand-wordmark,
          .sidebar.collapsed .sidebar-credits,
          .sidebar.collapsed .nav-section,
          .sidebar.collapsed .sidebar-subnav { display: none; }
          /* The group label carries the separation while the menu is open; on
             the rail the label is gone, so the rule appears in its place. */
          .sidebar.collapsed .nav-section-rule { display: block; }
          .sidebar.collapsed .sidebar-brand { justify-content: center; padding-left: 0; padding-right: 0; }
          .sidebar.collapsed .brand-lockup { justify-content: center; }
          .sidebar.collapsed .sidebar-nav a { justify-content: center; }

          /* Collapsed rail: no room beside the glyph, so hide the icon and swap
             it in on hover, signalling "click to expand". */
          .sidebar.collapsed .brand-expand-icon { display: none; }
          .sidebar.collapsed .brand-lockup:hover .brand-glyph { display: none; }
          /* On the rail the icon stands alone in the centre — drop the offset. */
          .sidebar.collapsed .brand-lockup:hover .brand-expand-icon { display: block; margin-left: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .sidebar { transition: none; }
          .sidebar a { transition: none !important; }
        }
        @media (max-width: 768px) {
          /* Mobile: full-width slide-in drawer toggled by the hamburger. */
          .sidebar {
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            z-index: 100;
            transform: translateX(-100%);
            box-shadow: none;
          }
          .sidebar.open {
            transform: translateX(0);
            box-shadow: 4px 0 24px rgba(0,0,0,0.35);
          }
        }
      `}</style>
      <aside className={`sidebar${open ? ' open' : ''}${collapsed ? ' collapsed' : ''}`}>
        {/* Brand lockup — brian glyph + wordmark. The logo doubles as the
            collapse/expand toggle, so the lockup is a <button>. */}
        <div className="sidebar-brand">
          <button
            type="button"
            className="brand-lockup"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Espandi menu' : 'Comprimi menu'}
            aria-expanded={!collapsed}
          >
            <svg className="brand-glyph" width="40" height="40" viewBox="0 0 44 44" fill={COLORS.accent} xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <path d="M38.2966 8.77426L32.5513 14.5408C30.7496 16.3492 28.1694 16.8634 25.8155 15.8849C23.4616 14.9065 22 12.711 22 10.1541V1.99927H26.4146V10.1541C26.4146 11.2526 27.2486 11.6854 27.5048 11.7911C27.7609 11.8968 28.6547 12.1839 29.4289 11.4068L35.1742 5.64031L38.2951 8.77283L38.2966 8.77426Z" />
              <path d="M14.6153 21.9971H6.49048V17.5662H14.6153C15.7097 17.5662 16.1409 16.7291 16.2462 16.472C16.3515 16.2149 16.6376 15.3178 15.8634 14.5408L10.1181 8.77425L13.2391 5.64172L18.9844 11.4082C20.7861 13.2166 21.2984 15.8063 20.3236 18.169C19.3487 20.5316 17.1613 21.9985 14.6139 21.9985L14.6153 21.9971Z" />
              <path d="M26.4146 37.564H22V29.4092C22 28.3108 21.166 27.8779 20.9098 27.7722C20.6537 27.6665 19.7599 27.3794 18.9857 28.1565L5.19252 42.0007L2.07581 38.8625L15.8633 25.024C17.6651 23.2156 20.2452 22.6999 22.5991 23.6798C24.953 24.6583 26.4146 26.8538 26.4146 29.4106V37.5655V37.564Z" />
              <path d="M35.1741 33.923L29.4288 28.1565C27.6271 26.3481 27.1148 23.7584 28.0896 21.3958C29.0645 19.0331 31.2519 17.5662 33.7993 17.5662H41.9241V21.9971H33.7993C32.7049 21.9971 32.2737 22.8342 32.1684 23.0913C32.0631 23.3484 31.777 24.2454 32.5512 25.0225L38.2965 30.789L35.1755 33.9216L35.1741 33.923Z" />
            </svg>
            <span className="brand-wordmark" style={{ fontSize: '24px', fontWeight: 700, color: COLORS.surface, fontFamily: FONT_SANS, letterSpacing: '-0.2px' }}>brian</span>
            {/* Expand affordance: revealed (swaps the glyph) only when the rail is
                collapsed AND the logo is hovered. */}
            <ExpandIcon className="brand-expand-icon" size={40} style={{ flexShrink: 0 }} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {!adminMode && mainItems.map(item => (
            <React.Fragment key={item.id}>
              <NavItem item={item} labelOf={labelOf} />
              {item.children?.map(child => (
                <NavLink
                  key={child.id}
                  to={child.path}
                  className="sidebar-subnav"
                  style={subNavStyle}
                  onMouseEnter={e => { e.currentTarget.style.color = COLORS.surface; }}
                  onMouseLeave={e => { e.currentTarget.style.color = COLORS.textTertiary; }}
                >
                  {labelOf(child)}
                </NavLink>
              ))}
            </React.Fragment>
          ))}
          {!adminMode && cortexItems.length > 0 && (
            <>
              {/* Hidden on the collapsed rail, where there is no room for a
                  word — the rule below takes its place so the two groups stay
                  visually separate at 72px. */}
              <hr className="nav-section-rule" style={divider} />
              <div className="nav-section" style={sectionHeading}>{t('nav.cortexSection')}</div>
              {cortexItems.map(item => (
                <NavItem key={item.id} item={item} labelOf={labelOf} />
              ))}
            </>
          )}
          {adminItems.length > 0 && (
            <>
              {!adminMode && <hr style={divider} />}
              {adminItems.map(item => (
                <NavItem key={item.id} item={item} labelOf={labelOf} />
              ))}
            </>
          )}
          {systemItems.length > 0 && (
            <>
              <hr style={divider} data-testid="nav-system-rule" />
              {systemItems.map(item => (
                <NavItem key={item.id} item={item} labelOf={labelOf} />
              ))}
            </>
          )}
        </nav>

        {/* Credits meter (Figma "HorizontalBorder" 260×113, padding 24/18 + top border).
            ⚠️ Mounted only outside admin mode. The meter renders null there anyway,
            but useEntitlements keys its fetch on the active tenant ALONE — it knows
            nothing of admin mode — so mounting it would ask /balance for a tenant the
            operator is not acting as. Not mounting is what keeps "admin mode never
            even asks" true now that the rail no longer owns its own fetch. */}
        {!adminMode && <CreditsMeter />}
      </aside>
    </>
  );
}
